import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  TablePagination,
  Menu,
  ListItemIcon,
  ListItemText,
  Box,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DownloadIcon from '@mui/icons-material/Download';
import GetAppIcon from '@mui/icons-material/GetApp';
import TableChartIcon from '@mui/icons-material/TableChart';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import SettingsIcon from '@mui/icons-material/Settings';
import { format } from 'date-fns';
import PredefinedFeedbackSettings from './PredefinedFeedbackSettings';
import { firebase } from 'common/src/config/configureFirebase';
import { onValue, query, orderByChild, startAt, endAt } from 'firebase/database';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  card: {
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e0e0e0',
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  ratingCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  star: {
    color: '#ffc107',
  },
  emptyStar: {
    color: '#e0e0e0',
  },
  driverCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  customerCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statusChip: {
    fontWeight: 'bold',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  tripIdCell: {
    minWidth: 120,
    maxWidth: 200,
  },
}));

const FeedbackReview = () => {
  console.log("🔍 FeedbackReview component loaded");
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const dispatch = useDispatch();

  // Redux state
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const predefiniedRatingFeedbackSetting = useSelector(state => state.predefiniedRatingFeedbackSetting);
  const userdata = useSelector((state) => state.usersdata);

  console.log("🔍 Auth state:", auth);
  console.log("🔍 Settings state:", settings);
  console.log("🔍 Feedback review module enabled:", settings?.feedback_review_module_enabled);
  console.log("🔍 Users data:", userdata?.users?.length || 0, 'users available');

  // Helper function to find user by ID and get email
  const findUserById = useCallback((userId) => {
    if (!userdata?.users || !userId) return null;
    
    const user = userdata.users.find(u => u.id === userId);
    return user ? {
      id: user.id,
      name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User',
      email: user.email || '',
      usertype: user.usertype || 'unknown'
    } : null;
  }, [userdata?.users]);

  // Get predefined feedback options from new path
  const getPredefinedFeedbackOptions = useCallback(() => {
    if (predefiniedRatingFeedbackSetting?.data) {
      // Handle both array and object formats
      let feedbackArray = [];
      
      if (Array.isArray(predefiniedRatingFeedbackSetting.data)) {
        feedbackArray = predefiniedRatingFeedbackSetting.data;
      } else if (typeof predefiniedRatingFeedbackSetting.data === 'object') {
        feedbackArray = Object.values(predefiniedRatingFeedbackSetting.data);
      }
      
      return feedbackArray.filter(option => option.isActive === true)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    
    return [];
  }, [predefiniedRatingFeedbackSetting?.data]);

  // Get feedback text from rating data (supports both new and old formats)
  const getFeedbackTexts = useCallback((rating) => {
    if (rating.predefinedFeedbackList && Array.isArray(rating.predefinedFeedbackList)) {
      // New format: array of objects
      return rating.predefinedFeedbackList.map(item => item.text);
    } else if (rating.predefinedFeedbackTags && Array.isArray(rating.predefinedFeedbackTags)) {
      // Old format: array of IDs - convert to text
      return rating.predefinedFeedbackTags.map(tagId => {
        const option = getPredefinedFeedbackOptions().find(option => option.id === tagId);
        return option ? option.text : `Tag ${tagId}`;
      });
    }
    return [];
  }, [getPredefinedFeedbackOptions]);

  // Local state
  const [loading, setLoading] = useState(false);
  const [ratings, setRatings] = useState([]);
  const [filteredRatings, setFilteredRatings] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDate, setFilterDate] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);





  // Fetch ratings data
  const fetchRatings = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔍 Starting to fetch ratings...');

      // Fetch ratings from Firebase database
      if (!firebase || !firebase.bookingRef) {
        console.error('Firebase not initialized');
        setSnackbar({
          open: true,
          message: 'Firebase not initialized',
          severity: 'error'
        });
        return;
      }

      // Get all bookings with ratings
      const ratingsQuery = query(
        firebase.bookingRef,
        orderByChild('rating')
      );

      onValue(ratingsQuery, (snapshot) => {
        const bookingsData = snapshot.val();
        const ratingsData = [];

        if (bookingsData) {
          Object.keys(bookingsData).forEach(bookingId => {
            const booking = bookingsData[bookingId];

            // Only include bookings that have ratings
            if (booking.rating && booking.rating > 0) {
              // Debug: Log available date fields
              console.log('🔍 Booking date fields:', {
                bookingId,
                bookingDate: booking.bookingDate,
                createdAt: booking.createdAt,
                updatedAt: booking.updatedAt,
                timestamp: booking.timestamp,
                date: booking.date,
                ratingDate: booking.ratingDate
              });
              
              // Get actual user data from Redux store
              const driverUser = findUserById(booking.driver);
              const customerUser = findUserById(booking.customer);
              
              const ratingData = {
                id: bookingId,
                rating: booking.rating,
                driver: {
                  id: booking.driver || '',
                  name: driverUser?.name || booking.driver_name || 'Unknown Driver',
                  email: driverUser?.email || booking.driver_email || '',
                  profileImage: booking.driver_image || null
                },
                customer: {
                  id: booking.customer || '',
                  name: customerUser?.name || booking.customer_name || 'Unknown Customer',
                  email: customerUser?.email || booking.customer_email || ''
                },
                serviceType: booking.carType || booking.category || 'taxi',
                category: booking.category || 'taxi',
                date: booking.bookingDate ? new Date(booking.bookingDate).toISOString() : 
                      (booking.createdAt ? new Date(booking.createdAt).toISOString() : 
                       (booking.updatedAt ? new Date(booking.updatedAt).toISOString() : 
                        (booking.timestamp ? new Date(booking.timestamp).toISOString() : 
                         (booking.date ? new Date(booking.date).toISOString() : 
                          (booking.ratingDate ? new Date(booking.ratingDate).toISOString() : 
                           new Date().toISOString()))))),
                tripCost: booking.trip_cost || 0,
                paymentMode: booking.payment_mode || 'unknown',
                pickupAddress: booking.pickup?.add || 'Unknown Pickup',
                dropAddress: booking.drop?.add || 'Unknown Drop',
                predefinedFeedbackTags: booking.predefinedFeedbackTags || [],
                predefinedFeedbackList: booking.predefinedFeedbackList || [],
                customNote: booking.customNote || '',
                driverComment: booking.driverComment || '',
                pointsUsed: booking.pointsUsed || 0
              };

              ratingsData.push(ratingData);
            }
          });
        }

        console.log('🔍 Fetched ratings from Firebase:', ratingsData.length, 'ratings');
        console.log('🔍 Sample rating data:', ratingsData[0]);
        setRatings(ratingsData);
      }, (error) => {
        console.error('Error fetching ratings from Firebase:', error);
        setSnackbar({
          open: true,
          message: 'Error fetching ratings from database',
          severity: 'error'
        });
      });

    } catch (error) {
      console.error('Error fetching ratings:', error);
      setSnackbar({
        open: true,
        message: 'Error loading ratings data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);


  // Apply search, date filter and default sorting to all ratings
  const applySearchAndSorting = useCallback(() => {
    let filtered = [...ratings];

    // Apply global search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(rating =>
        rating.id.toLowerCase().includes(query) || // Search by booking ID
        rating.driver.name.toLowerCase().includes(query) ||
        rating.driver.email.toLowerCase().includes(query) || // Search by driver email
        rating.customer.name.toLowerCase().includes(query) ||
        rating.customer.email.toLowerCase().includes(query) || // Search by customer email
        rating.serviceType.toLowerCase().includes(query) ||
        rating.category.toLowerCase().includes(query) ||
        rating.paymentMode.toLowerCase().includes(query) ||
        (rating.customNote && rating.customNote.toLowerCase().includes(query)) ||
        (rating.driverComment && rating.driverComment.toLowerCase().includes(query)) ||
        rating.rating.toString().includes(query) ||
        rating.tripCost.toString().includes(query) ||
        format(new Date(rating.date), 'MMM dd, yyyy HH:mm').toLowerCase().includes(query)
      );
    }

    // Apply date filter
    if (filterDate) {
      filtered = filtered.filter(rating => {
        const ratingDate = new Date(rating.date);
        const filterDateStart = new Date(filterDate);
        filterDateStart.setHours(0, 0, 0, 0);
        const filterDateEnd = new Date(filterDate);
        filterDateEnd.setHours(23, 59, 59, 999);
        return ratingDate >= filterDateStart && ratingDate <= filterDateEnd;
      });
    }

    // Apply default sorting by date (newest first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA; // Descending order (newest first)
    });

    console.log('🔍 Applied search and default sorting, filtered ratings count:', filtered.length);
    setFilteredRatings(filtered);
    setPage(0); // Reset to first page
  }, [ratings, searchQuery, filterDate]);


  // Download functions
  const downloadCSV = () => {
    const csvData = filteredRatings.map(rating => ({
      'Trip Id': rating.id,
      'User': rating.customer.name,
      'User Email': rating.customer.email || '-',
      'Driver': rating.driver.name,
      'Driver Email': rating.driver.email || '-',
      'Rating Stars': rating.rating,
      'Tags': getFeedbackTexts(rating).length > 0
        ? getFeedbackTexts(rating).join(', ')
        : '-',
      'Comment': rating.customNote || '-',
      'Driver Comment': rating.driverComment || '-',
      'Category': t(rating.category || 'category'),
      'Type of Service': t(rating.serviceType || 'service_type'),
      'Trip Cost': `$${rating.tripCost}`,
      'Point Used': rating.pointsUsed || 0,
      'Payment Mode': t(rating.paymentMode || 'payment_mode'),
      'Date and Time': format(new Date(rating.date), 'MMM dd, yyyy HH:mm')
    }));

    const headers = Object.keys(csvData[0] || {});
    const csvContent = [
      headers.join(','),
      ...csvData.map(row =>
        headers.map(header => `"${row[header] || ''}"`).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `feedback-review-${format(new Date(), 'yyyy-MM-dd-HH-mm-ss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSnackbar({
      open: true,
      message: t('csv_downloaded_successfully') || 'CSV file downloaded successfully!',
      severity: 'success'
    });
  };

  const downloadExcel = () => {
    // For Excel download, we'll use a simple approach with CSV format but .xlsx extension
    // In a real implementation, you might want to use a library like xlsx
    const csvData = filteredRatings.map(rating => ({
      'Trip Id': rating.id,
      'User': rating.customer.name,
      'User Email': rating.customer.email || '-',
      'Driver': rating.driver.name,
      'Driver Email': rating.driver.email || '-',
      'Rating Stars': rating.rating,
      'Tags': getFeedbackTexts(rating).length > 0
        ? getFeedbackTexts(rating).join(', ')
        : '-',
      'Comment': rating.customNote || '-',
      'Driver Comment': rating.driverComment || '-',
      'Category': t(rating.category || 'category'),
      'Type of Service': t(rating.serviceType || 'service_type'),
      'Trip Cost': `$${rating.tripCost}`,
      'Point Used': rating.pointsUsed || 0,
      'Payment Mode': t(rating.paymentMode || 'payment_mode'),
      'Date and Time': format(new Date(rating.date), 'MMM dd, yyyy HH:mm')
    }));

    const headers = Object.keys(csvData[0] || {});
    const csvContent = [
      headers.join(','),
      ...csvData.map(row =>
        headers.map(header => `"${row[header] || ''}"`).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `feedback-review-${format(new Date(), 'yyyy-MM-dd-HH-mm-ss')}.xlsx`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSnackbar({
      open: true,
      message: t('excel_downloaded_successfully') || 'Excel file downloaded successfully!',
      severity: 'success'
    });
  };

  const handleDownloadClick = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  // Render stars
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        i <= rating ? (
          <StarIcon key={i} className={classes.star} fontSize="small" />
        ) : (
          <StarBorderIcon key={i} className={classes.emptyStar} fontSize="small" />
        )
      );
    }
    return stars;
  };

  // Get paginated data
  const paginatedRatings = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredRatings.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredRatings, page, rowsPerPage]);

  // Handle search change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Clear date filter
  const clearDateFilter = () => {
    setFilterDate(null);
  };


  // Load data on component mount
  useEffect(() => {
    fetchRatings();
  }, [fetchRatings]);

  // Ensure users data is loaded
  useEffect(() => {
    if (!userdata?.users) {
      console.log('🔄 Users data not available, dispatching fetchUsers...');
      dispatch({ type: 'FETCH_USERS' });
    }
  }, [userdata?.users, dispatch]);

  // Fetch predefined feedback data from Firebase on component mount (like PredefinedFeedbackSettings.js)
  useEffect(() => {
    const fetchPredefinedFeedbackFromFirebase = async () => {
      try {
        console.log('🔄 Fetching predefined feedback data from Firebase...');
        const { firebase } = require('common/src/config/configureFirebase');
        const { onValue, ref } = require("firebase/database");

        const feedbackRef = ref(firebase.database, 'predefiniedRatingFeedbackSetting');

        onValue(feedbackRef, (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val();
            console.log('📥 Predefined feedback data received from Firebase:', data);

            // Convert object to array if needed
            let feedbackArray = [];
            if (Array.isArray(data)) {
              feedbackArray = data;
            } else if (typeof data === 'object') {
              feedbackArray = Object.values(data);
            }

            if (feedbackArray.length > 0) {
              const feedbackOptions = feedbackArray.map((item, index) => ({
                id: item.id || `feedback_${index}`,
                text: item.text || '',
                isActive: item.isActive !== false,
                order: item.order || (index + 1)
              }));
              const sortedOptions = feedbackOptions.sort((a, b) => a.order - b.order);
              console.log('✅ Processed predefined feedback options from Firebase:', sortedOptions);
              
              // Update Redux state with new data
              dispatch({
                type: 'FETCH_PREDEFINED_FEEDBACK_SUCCESS',
                payload: sortedOptions
              });
            } else {
              console.log('📭 No predefined feedback data in Firebase');
            }
          } else {
            console.log('📭 No predefined feedback data in Firebase');
          }
        }, (error) => {
          console.error('❌ Error fetching predefined feedback data from Firebase:', error);
        });
      } catch (error) {
        console.error('❌ Error setting up Firebase listener for predefined feedback:', error);
      }
    };

    fetchPredefinedFeedbackFromFirebase();
  }, [dispatch]);

  // Apply search and sorting when ratings, searchQuery, or sortConfig change
  useEffect(() => {
    applySearchAndSorting();
  }, [applySearchAndSorting]);

  if (loading && ratings.length === 0) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  console.log("🔍 FeedbackReview rendering...");
  console.log("🔍 Ratings data:", ratings);

  return (
    <div className={classes.root}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Typography
          variant="h4"
          component="h1"
          style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left', margin: 0 }}
        >
          {t('feedback_review') || 'Feedback Review'}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setSettingsModalOpen(true)}
          size="medium"
          color="primary"
        >
          {t('predefined_feedback_tags') || 'Predefined Feedback Tags'}
        </Button>
      </div>



      {/* Results */}
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Typography variant="h6">
              {t('rating_results') || 'Result'} ({filteredRatings.length})
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <TextField
                label={t('search') || 'Search'}
                placeholder={t('search') || 'Search'}
                value={searchQuery}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon style={{ marginRight: 8, color: '#666' }} />,
                  endAdornment: searchQuery && (
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery('')}
                      style={{ marginRight: 8 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )
                }}
                style={{ minWidth: 300 }}
              />

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t('date') || 'Date'}
                  value={filterDate}
                  onChange={(newValue) => setFilterDate(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      style={{ minWidth: 180 }}
                    />
                  )}
                />
              </LocalizationProvider>

              {filterDate && (
                <Tooltip title={t('clear_date_filter') || 'Clear Date Filter'}>
                  <IconButton
                    size="small"
                    onClick={clearDateFilter}
                    style={{ color: '#666' }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title={t('download_data') || 'Download Data'}>
                <IconButton
                  onClick={handleDownloadClick}
                  size="small"
                  disabled={filteredRatings.length === 0}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          {paginatedRatings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
              <Typography variant="body1">
                {searchQuery ?
                  (t('no_feedback_found') || 'No feedback found matching your search.') :
                  (t('loading') || 'loading')
                }
              </Typography>
            </div>
          ) : (
            <>
              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table className={classes.table} aria-label="ratings table">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tripIdCell}>{t('order_id') || 'Order Id'}</TableCell>
                      <TableCell>{t('user') || 'User'}</TableCell>
                      <TableCell>{t('driver') || 'Driver'}</TableCell>
                      <TableCell>{t('ratings') || 'Ratings'}</TableCell>
                      <TableCell>{t('tags') || 'Tags'}</TableCell>
                      <TableCell style={{ minWidth: 250 }}>{t('user_comment') || 'User Comment'}</TableCell>
                      <TableCell style={{ minWidth: 250 }}>{t('driver_comment') || 'Driver Comment'}</TableCell>
                      <TableCell>{t('category') || 'Category'}</TableCell>
                      <TableCell>{t('type_of_service') || 'Type of Service'}</TableCell>
                      <TableCell>{t('trip_cost') || 'Trip Cost'}</TableCell>
                      <TableCell>{t('point_used') || 'Point Used'}</TableCell>
                      <TableCell>{t('payment_mode') || 'Payment Mode'}</TableCell>
                      <TableCell style={{ minWidth: 180 }}>{t('date') || 'Date'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRatings.map((rating) => (
                      <TableRow key={rating.id} hover>
                        <TableCell className={classes.tripIdCell}>
                          <Typography variant="body2" style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {rating.id}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ minWidth: 200 }}>
                          <div className={classes.customerCell}>
                            <Avatar sx={{ width: 32, height: 32 }}>
                              <PersonIcon />
                            </Avatar>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                              <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                {rating.customer.name}
                              </Typography>
                              {rating.customer.email && (
                                <Typography variant="caption" style={{ color: '#666', fontSize: '0.75rem' }}>
                                  {rating.customer.email}
                                </Typography>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell style={{ minWidth: 200 }}>
                          <div className={classes.driverCell}>
                            <Avatar
                              src={rating.driver.profileImage}
                              sx={{ width: 32, height: 32 }}
                            >
                              <PersonIcon />
                            </Avatar>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                              <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                {rating.driver.name}
                              </Typography>
                              {rating.driver.email && (
                                <Typography variant="caption" style={{ color: '#666', fontSize: '0.75rem' }}>
                                  {rating.driver.email}
                                </Typography>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className={classes.ratingCell}>
                            {renderStars(rating.rating)}
                            <Typography variant="body2" style={{ marginLeft: 8 }}>
                              {rating.rating}/5
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div style={{ maxWidth: 200 }}>
                            {getFeedbackTexts(rating).length > 0 ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {getFeedbackTexts(rating).map((tagText, index) => (
                                  <Chip
                                    key={`feedback_${index}`}
                                    label={tagText}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    style={{ fontSize: '0.7rem', height: 24 }}
                                  />
                                ))}
                              </div>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                -
                              </Typography>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div style={{ maxWidth: 300, minWidth: 250 }}>
                            {rating.customNote && rating.customNote.trim() ? (
                              <Typography
                                variant="body2"
                                style={{
                                  wordBreak: 'break-word',
                                  backgroundColor: '#e8f5e8',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  borderLeft: '3px solid #4caf50'
                                }}
                              >
                                "{rating.customNote}"
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                -
                              </Typography>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div style={{ maxWidth: 300, minWidth: 250 }}>
                            {rating.driverComment && rating.driverComment.trim() ? (
                              <Typography
                                variant="body2"
                                style={{
                                  wordBreak: 'break-word',
                                  backgroundColor: '#e3f2fd',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  borderLeft: '3px solid #2196f3'
                                }}
                              >
                                "{rating.driverComment}"
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                -
                              </Typography>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t(rating.category || 'category')}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t(rating.serviceType || 'service_type')}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            ${rating.tripCost}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                            {rating.pointsUsed || 0}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t(rating.paymentMode || 'payment_mode')}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell style={{ minWidth: 180 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <AccessTimeIcon fontSize="small" />
                            <Typography variant="body2">
                              {format(new Date(rating.date), 'MMM dd, yyyy HH:mm')}
                            </Typography>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={filteredRatings.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('rows_per_page') || 'Rows per page:'}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} ${t('of') || 'of'} ${count !== -1 ? count : `${t('more_than') || 'more than'} ${to}`}`
                }
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Download Menu */}
      <Menu
        anchorEl={downloadAnchorEl}
        open={Boolean(downloadAnchorEl)}
        onClose={handleDownloadClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={() => { downloadCSV(); handleDownloadClose(); }}>
          <ListItemIcon>
            <TableChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {t('export_csv') || 'Export CSV'}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { downloadExcel(); handleDownloadClose(); }}>
          <ListItemIcon>
            <GetAppIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {t('export_excel') || 'Export Excel'}
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Predefined Feedback Settings Modal */}
      <Dialog
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: {
            maxHeight: '95vh',
            height: '95vh',
            margin: '16px',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogContent 
          style={{ 
            padding: 0, 
            overflow: 'hidden',
            backgroundColor: '#fff'
          }}
        >
          <PredefinedFeedbackSettings />
        </DialogContent>
        <DialogActions 
          style={{ 
            padding: '16px 24px',
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#f8f9fa',
            justifyContent: 'flex-end'
          }}
        >
          <Button 
            onClick={() => setSettingsModalOpen(false)}
            color="primary"
            variant="contained"
            size="large"
            style={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: '500',
              padding: '8px 24px'
            }}
          >
            {t('close') || 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FeedbackReview;
